/**
 * Configuración de Point Entities asociables a la red canónica (Frontend mirror)
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  FUENTE DE VERDAD: BACKEND                                                   ║
 * ║                                                                              ║
 * ║  Este archivo es un MIRROR de:                                               ║
 * ║  backend/src/spatial/config/point-entities.config.ts                         ║
 * ║                                                                              ║
 * ║  NO modificar valores aquí sin actualizar primero el backend.                ║
 * ║  El backend valida las reglas de negocio - si hay discrepancia,              ║
 * ║  el backend rechazará operaciones y la UI mostrará errores.                  ║
 * ║                                                                              ║
 * ║  Para consultar config autoritativa en runtime:                              ║
 * ║  GET /api/v1/point-entity-types                                              ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see backend/src/spatial/config/point-entities.config.ts (FUENTE DE VERDAD)
 * @see ARQUITECTURA_ASOCIACION_PUNTO_RED.md
 * @updated 2026-03-23 - Agregado container_observation, hotspot_observation
 * @updated 2026-03-22 - Sincronizado con backend, fixes #1, #2, #3
 */

/**
 * Formato de geometría de la entidad
 */
export type GeometryFormat = 'coordinates' | 'geojson' | 'postgis';

/**
 * Configuración completa de una Point Entity asociable a red
 */
export interface PointEntityConfig {
  /** Identificador único del tipo (usado en entity_type) */
  entityType: string;

  /** Módulo al que pertenece */
  module: string;

  /** Nombre de la tabla en la base de datos */
  tableName: string;

  /** Si se debe auto-asociar al crear la entidad */
  autoAssociateOnCreate: boolean;

  /** Si admite recálculo automático cuando la red cambia */
  allowsAutoRecalculation: boolean;

  /** Si admite bloqueo manual */
  allowsManualLock: boolean;

  /** Si admite reasociación manual por el usuario */
  allowsManualReassociation: boolean;

  /**
   * Si permite múltiples conexiones a la red.
   * IMPORTANTE: Este valor DEBE coincidir con backend.
   * Si backend dice false y frontend dice true, el usuario verá
   * botón "Agregar conexión" pero backend rechazará con 400.
   */
  allowMultipleConnections?: boolean;

  /** Label singular para UI */
  label: string;

  /** Label plural para UI */
  labelPlural: string;

  /** Icono (nombre de Lucide icon) */
  icon: string;
}

/**
 * Registry de Point Entities
 *
 * SINCRONIZADO CON BACKEND 2026-03-23
 * Cualquier cambio aquí DEBE reflejarse primero en backend.
 */
export const POINT_ENTITY_REGISTRY: PointEntityConfig[] = [
  // ═══════════════════════════════════════════════════════════════
  // MOBILITY MODULE
  // ═══════════════════════════════════════════════════════════════
  {
    entityType: 'transit_stop',
    module: 'mobility',
    tableName: 'transit_stops',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: true,
    allowsManualReassociation: true,
    allowMultipleConnections: false, // Backend: false
    label: 'Parada',
    labelPlural: 'Paradas',
    icon: 'BusFront',
  },

  // ═══════════════════════════════════════════════════════════════
  // ENVIRONMENTAL MODULE
  // ═══════════════════════════════════════════════════════════════
  {
    entityType: 'tree',
    module: 'environmental',
    tableName: 'trees',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false, // FIX #2: Backend dice false, no true
    allowsManualReassociation: true,
    allowMultipleConnections: false, // FIX #1: Backend dice false, no true
    label: 'Árbol',
    labelPlural: 'Árboles',
    icon: 'TreeDeciduous',
  },

  // ═══════════════════════════════════════════════════════════════
  // INFRASTRUCTURE MODULE
  // ═══════════════════════════════════════════════════════════════
  {
    entityType: 'streetlight',
    module: 'infrastructure',
    tableName: 'streetlights',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false,
    allowsManualReassociation: true,
    allowMultipleConnections: false, // Backend: false
    label: 'Luminaria',
    labelPlural: 'Luminarias',
    icon: 'Lightbulb',
  },

  // ═══════════════════════════════════════════════════════════════
  // WASTE MODULE
  // ═══════════════════════════════════════════════════════════════
  {
    entityType: 'waste_container',
    module: 'waste',
    tableName: 'waste_containers',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false, // Backend: false
    allowsManualReassociation: true,
    allowMultipleConnections: false, // FIX #1: Backend dice false, no true
    label: 'Contenedor',
    labelPlural: 'Contenedores',
    icon: 'Trash2',
  },
  // FIX #3: Agregar waste_depot (faltaba en frontend)
  {
    entityType: 'waste_depot',
    module: 'waste',
    tableName: 'waste_depots',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false,
    allowsManualReassociation: true,
    allowMultipleConnections: false,
    label: 'Depósito',
    labelPlural: 'Depósitos',
    icon: 'Warehouse',
  },
  // FIX #3: Agregar waste_hotspot (faltaba en frontend)
  {
    entityType: 'waste_hotspot',
    module: 'waste',
    tableName: 'waste_hotspots',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false,
    allowsManualReassociation: true,
    allowMultipleConnections: false,
    label: 'Microbasural',
    labelPlural: 'Microbasurales',
    icon: 'AlertTriangle',
  },
  // Observaciones de contenedor (relevamientos de campo)
  {
    entityType: 'container_observation',
    module: 'waste',
    tableName: 'container_observations',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false,
    allowsManualReassociation: false,
    allowMultipleConnections: false,
    label: 'Obs. Contenedor',
    labelPlural: 'Obs. Contenedores',
    icon: 'ClipboardCheck',
  },
  // Observaciones de microbasural
  {
    entityType: 'hotspot_observation',
    module: 'waste',
    tableName: 'hotspot_observations',
    autoAssociateOnCreate: true,
    allowsAutoRecalculation: true,
    allowsManualLock: false,
    allowsManualReassociation: false,
    allowMultipleConnections: false,
    label: 'Obs. Microbasural',
    labelPlural: 'Obs. Microbasurales',
    icon: 'ClipboardList',
  },

  // ═══════════════════════════════════════════════════════════════
  // URBAN MODULE
  // ═══════════════════════════════════════════════════════════════
  {
    entityType: 'building_centroid',
    module: 'urban',
    tableName: 'building_centroids',
    autoAssociateOnCreate: false,
    allowsAutoRecalculation: false,
    allowsManualLock: false,
    allowsManualReassociation: false,
    allowMultipleConnections: false,
    label: 'Edificio',
    labelPlural: 'Edificios',
    icon: 'Building2',
  },
  {
    entityType: 'fountain',
    module: 'urban',
    tableName: 'fountains',
    autoAssociateOnCreate: false, // Fuentes no siempre están en vía
    allowsAutoRecalculation: false,
    allowsManualLock: false,
    allowsManualReassociation: true, // Solo manual
    allowMultipleConnections: false,
    label: 'Fuente',
    labelPlural: 'Fuentes',
    icon: 'Waves',
  },
];

/**
 * Obtiene la configuración de una Point Entity por su tipo
 */
export function getPointEntityConfig(entityType: string): PointEntityConfig | undefined {
  return POINT_ENTITY_REGISTRY.find((e) => e.entityType === entityType);
}

/**
 * Obtiene todas las Point Entities de un módulo
 */
export function getPointEntitiesByModule(module: string): PointEntityConfig[] {
  return POINT_ENTITY_REGISTRY.filter((e) => e.module === module);
}

/**
 * Verifica si un tipo de entidad es válido
 */
export function isValidPointEntityType(entityType: string): boolean {
  return POINT_ENTITY_REGISTRY.some((e) => e.entityType === entityType);
}

/**
 * Obtiene todos los tipos de entidad registrados
 */
export function getPointEntityTypes(): string[] {
  return POINT_ENTITY_REGISTRY.map((e) => e.entityType);
}
